import { Component, h, Prop, State } from '@stencil/core'
import { type DebugSettings, type EnvironmentOption, type StagingOption, storage } from '../utils/storage'

@Component({
  tag: 'rtr-env-select',
  shadow: false
})
export class EnvSelect {
  @Prop() mode!: 'environment' | 'staging'
  @Prop() asInlineText: boolean = false
  @Prop() environmentOptions: EnvironmentOption[] = []
  @Prop() stagingOptions: StagingOption[] = []
  @Prop() baseUrls?: { localhost: string; staging: string }
  @Prop() urlPattern?: string

  @State() debugSettings: DebugSettings = storage.getDebugSettings()

  componentDidLoad() {
    window.addEventListener('debug-settings-changed', this.handleSettingsChange)
  }

  disconnectedCallback() {
    window.removeEventListener('debug-settings-changed', this.handleSettingsChange)
  }

  private handleSettingsChange = (event: Event) => {
    this.debugSettings = (event as CustomEvent<DebugSettings>).detail
  }

  private updateEnvironmentSetting(value: DebugSettings['environment']) {
    if (!this.baseUrls || !this.urlPattern) {
      console.error('EnvSelect: baseUrls and urlPattern are required')
      return
    }

    const settings = { ...this.debugSettings }

    if (value === 'localhost') {
      settings.apiBaseUrl = this.baseUrls.localhost
    } else if (value === 'staging') {
      const currentStaging = settings.staging
      if (currentStaging === 'staging') {
        settings.apiBaseUrl = this.baseUrls.staging
      } else {
        settings.apiBaseUrl = this.urlPattern.replace('{staging}', currentStaging)
      }
    } else if (value === 'production') {
      settings.apiBaseUrl = 'https://app.rentware.io'
    }
    settings.environment = value

    storage.setDebugSettings(settings)
    this.debugSettings = settings
  }

  private updateStagingSetting(value: DebugSettings['staging']) {
    if (!this.baseUrls || !this.urlPattern) {
      console.error('EnvSelect: baseUrls and urlPattern are required')
      return
    }

    const settings = { ...this.debugSettings }

    if (value === 'staging') {
      settings.apiBaseUrl = this.baseUrls.staging
    } else {
      settings.apiBaseUrl = this.urlPattern.replace('{staging}', value)
    }

    settings.environment = 'staging'
    settings.staging = value

    storage.setDebugSettings(settings)
    this.debugSettings = settings
  }

  private handleChange = (event: Event) => {
    const value = (event.target as HTMLSelectElement).value

    if (this.mode === 'environment') {
      this.updateEnvironmentSetting(value as DebugSettings['environment'])
    } else {
      this.updateStagingSetting(value as DebugSettings['staging'])
    }
  }

  render() {
    const selectedValue = this.mode === 'environment' ? this.debugSettings.environment : this.debugSettings.staging

    const label = this.mode === 'environment' ? 'Environment' : 'Staging'
    const options = this.mode === 'environment' ? this.environmentOptions : this.stagingOptions

    const disabled = this.mode === 'staging' && this.debugSettings.environment === 'localhost'

    // If no options provided, show loading state
    if (options.length === 0) {
      return (
        <div class={`debug-toolbar-control ${this.asInlineText ? 'inline-text' : ''}`}>
          <label htmlFor={`debug-${this.mode}-select`}>{label}</label>
          <select disabled>
            <option>Loading...</option>
          </select>
        </div>
      )
    }

    return (
      <div class={`debug-toolbar-control ${this.asInlineText ? 'inline-text' : ''}`}>
        <label htmlFor={`debug-${this.mode}-select`}>{label}</label>
        <select
          id={`debug-${this.mode}-select`}
          onChange={this.handleChange}
          disabled={disabled}
        >
          {options.map(option => (
            <option
              value={option.value}
              selected={selectedValue === option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>
    )
  }
}
